import { useState } from 'react'
import { Sparkles, Loader2, ChevronDown, ChevronRight, User, Check, Flag, Edit3, X, Tag, AlertTriangle, AlertCircle, BrainCircuit, RefreshCw, Copy, Eye } from 'lucide-react'
import { rfpApi } from '@/api/client'
import { showToast } from '@/store/uiStore'
import type { RfpRequirement, RfpTeamMember, RfpFit } from '../types'
import { FIT_CONFIG, STATUS_CONFIG, CONFIDENCE_CONFIG } from '../types'

interface RequirementsTableProps {
  requirements: RfpRequirement[]
  teamMembers: RfpTeamMember[]
  hasReferences: boolean
  onUpdate: () => void
  selfEvalOptions?: string[] | null
}

export function RequirementsTable({ requirements, teamMembers, hasReferences, onUpdate, selfEvalOptions }: RequirementsTableProps) {
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [searchingId, setSearchingId] = useState<number | null>(null)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editText, setEditText] = useState('')
  const [filterFit, setFilterFit] = useState<string>('all')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [searchText, setSearchText] = useState('')
  const [justProcessed, setJustProcessed] = useState<Set<number>>(new Set())
  const [reprocessingId, setReprocessingId] = useState<number | null>(null)

  const handleAiSearch = async (req: RfpRequirement) => {
    if (!hasReferences) {
      showToast('Upload reference documents first', 'error')
      return
    }
    setSearchingId(req.id)
    try {
      const res = await rfpApi.aiSearch(req.id)
      if (res.success && res.data) {
        const data = res.data as Record<string, unknown>
        if (data.message) {
          showToast(data.message as string, 'error')
        } else {
          // Auto-expand to show the result and flash a success indicator
          setExpandedId(req.id)
          setJustProcessed(prev => new Set([...prev, req.id]))
          setTimeout(() => setJustProcessed(prev => { const next = new Set(prev); next.delete(req.id); return next }), 2000)
        }
      } else {
        showToast(res.error || 'AI search failed', 'error')
      }
      onUpdate()
    } catch (err) {
      showToast('AI search failed', 'error')
    }
    setSearchingId(null)
  }

  const handleAssign = async (reqId: number, member: RfpTeamMember | null) => {
    await rfpApi.updateRequirement(reqId, {
      assigned_to: member?.email || null,
      assigned_to_name: member?.name || null,
      status: member ? 'assigned' : 'pending',
    })
    onUpdate()
  }

  const handleFitChange = async (reqId: number, fit: RfpFit) => {
    await rfpApi.updateRequirement(reqId, { fit })
    onUpdate()
  }

  const handleApprove = async (reqId: number) => {
    await rfpApi.updateRequirement(reqId, { status: 'approved', confidence: 'high' } as any)
    showToast('Response approved', 'success')
    onUpdate()
  }

  const handleFlag = async (reqId: number) => {
    await rfpApi.updateRequirement(reqId, { status: 'flagged' })
    showToast('Requirement flagged for review', 'success')
    onUpdate()
  }

  const handleSaveEdit = async (reqId: number) => {
    await rfpApi.updateRequirement(reqId, { final_response: editText, status: 'approved', confidence: 'high' } as any)
    setEditingId(null)
    showToast('Response saved', 'success')
    onUpdate()
  }

  const handleReprocess = async (req: RfpRequirement) => {
    setReprocessingId(req.id)
    try {
      const res = await rfpApi.reprocessRequirement(req.id, req.gap_notes || undefined)
      if (res.success) {
        showToast('Response reprocessed with feedback', 'success')
        setExpandedId(req.id)
        setJustProcessed(prev => new Set([...prev, req.id]))
        setTimeout(() => setJustProcessed(prev => { const next = new Set(prev); next.delete(req.id); return next }), 2000)
      } else {
        showToast(res.error || 'Reprocess failed', 'error')
      }
      onUpdate()
    } catch {
      showToast('Reprocess failed', 'error')
    }
    setReprocessingId(null)
  }

  // Build set of parent IDs that have children — these are decomposed grouping containers
  const parentIdsWithChildren = new Set<number>()
  for (const r of requirements) {
    if (r.parent_requirement_id) parentIdsWithChildren.add(r.parent_requirement_id)
  }

  // Filter (skip parent requirements that have sub-questions)
  const filtered = requirements.filter(r => {
    if (parentIdsWithChildren.has(r.id)) return false
    if (filterFit !== 'all' && r.fit !== filterFit) return false
    if (filterStatus === 'no_response') {
      if ((r.ai_response && r.ai_response.trim().length > 0) || (r.brain_response && r.brain_response.trim().length > 0)) return false
    } else if (filterStatus !== 'all' && r.status !== filterStatus) return false
    if (searchText) {
      const q = searchText.toLowerCase()
      const matchesSearch = r.requirement_text.toLowerCase().includes(q)
        || (r.section || '').toLowerCase().includes(q)
        || (r.sheet_name || '').toLowerCase().includes(q)
        || (r.requirement_name || '').toLowerCase().includes(q)
      if (!matchesSearch) return false
    }
    return true
  })

  const noResponseCount = requirements.filter(r => (!r.ai_response || r.ai_response.trim().length === 0) && (!r.brain_response || r.brain_response.trim().length === 0)).length

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <input
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          placeholder="Search requirements..."
          className="flex-1 min-w-[200px] rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[var(--accent-color)]"
        />
        <select
          value={filterFit}
          onChange={e => setFilterFit(e.target.value)}
          className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-color)]"
        >
          <option value="all">All Fits</option>
          {Object.entries(FIT_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-color)]"
        >
          <option value="all">All Statuses</option>
          <option value="no_response">No Response ({noResponseCount})</option>
          {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <span className="text-xs text-[var(--text-muted)]">
          {filtered.length} of {requirements.length}
        </span>
      </div>

      {/* Requirements list */}
      <div className="space-y-1">
        {filtered.map(req => {
            const isExpanded = expandedId === req.id
            const isSearching = searchingId === req.id
            const isEditing = editingId === req.id
            const fitCfg = FIT_CONFIG[req.fit] || FIT_CONFIG.tbd
            const statusCfg = STATUS_CONFIG[req.status]
            const displayResponse = req.final_response || req.ai_response

            return (
              <div
                key={req.id}
                className={`rounded-lg border overflow-hidden transition-colors ${
                  isSearching
                    ? 'border-violet-500/30 bg-violet-500/5'
                    : justProcessed.has(req.id)
                      ? 'border-emerald-500/30 bg-emerald-500/5'
                      : 'border-[var(--border-color)] bg-[var(--bg-secondary)]'
                }`}
              >
                {/* Row header */}
                <div
                  onClick={() => setExpandedId(isExpanded ? null : req.id)}
                  className="flex items-start gap-3 p-3 cursor-pointer hover:bg-[var(--bg-tertiary)] transition-colors"
                >
                  <span className="text-[10px] text-[var(--text-muted)] font-mono mt-0.5 shrink-0 w-8 text-right">
                    {req.parent_requirement_id && req.sub_question_index
                      ? `${req.row_index}${String.fromCharCode(96 + req.sub_question_index)}`
                      : req.row_index}
                  </span>
                  {isExpanded ? <ChevronDown size={14} className="text-[var(--text-muted)] mt-0.5 shrink-0" /> : <ChevronRight size={14} className="text-[var(--text-muted)] mt-0.5 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    {(req.sheet_name || req.section || req.requirement_name) && (
                      <p className="text-[10px] text-[var(--text-muted)] mb-0.5 truncate">
                        {[req.sheet_name, req.section, req.requirement_name].filter(Boolean).join(' › ')}
                      </p>
                    )}
                    <p className="text-sm text-[var(--text-primary)] leading-snug">
                      {req.parent_requirement_id && <span className="text-amber-400 mr-1">↳</span>}
                      {req.requirement_text}
                    </p>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      {req.requirement_level && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-500/10 text-blue-400">
                          {req.requirement_level}
                        </span>
                      )}
                      {req.fit !== 'tbd' && (
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${fitCfg.bg} ${fitCfg.color}`}>
                          {fitCfg.label}
                        </span>
                      )}
                      {req.confidence && CONFIDENCE_CONFIG[req.confidence] && (
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${CONFIDENCE_CONFIG[req.confidence].bg} ${CONFIDENCE_CONFIG[req.confidence].color}`}>
                          {CONFIDENCE_CONFIG[req.confidence].label} confidence
                        </span>
                      )}
                      {req.status !== 'pending' && (
                        <span className={`text-[10px] font-medium ${statusCfg.color}`}>
                          {statusCfg.label}
                        </span>
                      )}
                      {req.source === 'reference' && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-cyan-500/10 text-cyan-400">Samples</span>
                      )}
                      {req.source === 'cassidy' && req.confidence === 'high' && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-500/10 text-emerald-400">Samples + KB</span>
                      )}
                      {req.source === 'cassidy' && req.confidence !== 'high' && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-500/10 text-purple-400">KB</span>
                      )}
                      {req.source !== 'pending' && !req.ai_response && (
                        <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-500/10 text-red-400" title="AI processed this requirement but produced no response">
                          <AlertCircle size={9} />
                          No Response
                        </span>
                      )}
                      {req.gap_notes && (
                        <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/10 text-amber-400" title={req.gap_notes}>
                          <AlertTriangle size={9} />
                          Gap
                        </span>
                      )}
                      {req.tags && (
                        <span className="flex items-center gap-1 text-[10px] text-violet-400">
                          <Tag size={9} />
                          {req.tags}
                        </span>
                      )}
                      {req.self_eval && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-cyan-500/10 text-cyan-400">
                          {req.self_eval}
                        </span>
                      )}
                      {req.assigned_to_name && (
                        <span className="flex items-center gap-1 text-[10px] text-[var(--text-muted)]">
                          <User size={10} />
                          {req.assigned_to_name}{req.assigned_category ? ` (${req.assigned_category})` : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Quick AI search button */}
                  <button
                    onClick={e => { e.stopPropagation(); handleAiSearch(req) }}
                    disabled={isSearching || !hasReferences}
                    className={`flex items-center gap-1 px-2 py-1 text-[10px] rounded-md transition-colors shrink-0 ${
                      isSearching
                        ? 'bg-violet-600/30 text-violet-300 cursor-wait'
                        : 'bg-violet-600/20 text-violet-400 hover:bg-violet-600/30 disabled:opacity-40'
                    }`}
                    title={hasReferences ? 'Search references with AI' : 'Upload reference documents first'}
                  >
                    {isSearching ? <><Loader2 size={10} className="animate-spin" /> Searching...</> : <><Sparkles size={10} /> AI</>}
                  </button>
                </div>

                {/* Expanded detail */}
                {isExpanded && (
                  <div className="border-t border-[var(--border-color)] p-3 space-y-3 bg-[var(--bg-primary)]">
                    {/* AI / Final response */}
                    {displayResponse ? (
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider">
                            {req.final_response ? 'Final Response' : 'AI Draft'} ({req.source === 'reference' ? 'from samples' : req.source === 'cassidy' && req.confidence === 'high' ? 'samples + KB' : req.source === 'cassidy' ? 'from KB' : req.source === 'cassidy_no_answer' ? 'no KB match' : req.source})
                          </span>
                          <div className="flex items-center gap-1">
                            {!isEditing && (
                              <>
                                <button
                                  onClick={() => { navigator.clipboard.writeText(displayResponse); showToast('Copied to clipboard', 'info') }}
                                  className="p-1 text-[var(--text-muted)] hover:text-blue-400 transition-colors"
                                  title="Copy response"
                                >
                                  <Copy size={12} />
                                </button>
                                <button
                                  onClick={() => { setEditingId(req.id); setEditText(displayResponse) }}
                                  className="p-1 text-[var(--text-muted)] hover:text-blue-400 transition-colors"
                                  title="Edit response"
                                >
                                  <Edit3 size={12} />
                                </button>
                                <button
                                  onClick={() => handleApprove(req.id)}
                                  className="p-1 text-[var(--text-muted)] hover:text-green-400 transition-colors"
                                  title="Approve"
                                >
                                  <Check size={12} />
                                </button>
                                <button
                                  onClick={() => handleFlag(req.id)}
                                  className="p-1 text-[var(--text-muted)] hover:text-red-400 transition-colors"
                                  title="Flag for review"
                                >
                                  <Flag size={12} />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                        {isEditing ? (
                          <div className="space-y-2">
                            <textarea
                              value={editText}
                              onChange={e => setEditText(e.target.value)}
                              rows={4}
                              className="w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-color)]"
                            />
                            <div className="flex items-center gap-2 justify-end">
                              <button
                                onClick={() => setEditingId(null)}
                                className="flex items-center gap-1 px-2 py-1 text-xs text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] rounded transition-colors"
                              >
                                <X size={12} />Cancel
                              </button>
                              <button
                                onClick={() => handleSaveEdit(req.id)}
                                className="flex items-center gap-1 px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-500 transition-colors"
                              >
                                <Check size={12} />Save
                              </button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-[var(--text-secondary)] leading-relaxed whitespace-pre-wrap">
                            {displayResponse}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-[var(--text-muted)] italic">
                        No response yet. Click the AI button to search reference documents.
                      </p>
                    )}

                    {/* Gap warning (internal only) */}
                    {req.gap_notes && (
                      <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="flex items-center gap-1.5 text-[10px] font-semibold text-amber-400 uppercase tracking-wider">
                            <AlertTriangle size={10} />
                            Gap Warning (internal — do not include in response)
                          </span>
                          {req.status === 'flagged' && (
                            <button
                              onClick={() => handleReprocess(req)}
                              disabled={reprocessingId === req.id}
                              className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium border transition-colors ${
                                reprocessingId === req.id
                                  ? 'bg-amber-500/20 text-amber-300 border-amber-500/30 cursor-wait'
                                  : 'bg-amber-500/10 text-amber-400 border-amber-500/20 hover:bg-amber-500/20'
                              }`}
                            >
                              <RefreshCw size={10} className={reprocessingId === req.id ? 'animate-spin' : ''} />
                              {reprocessingId === req.id ? 'Reprocessing...' : 'Reprocess with feedback'}
                            </button>
                          )}
                        </div>
                        <p className="text-sm text-amber-300/80 leading-relaxed whitespace-pre-wrap">{req.gap_notes}</p>
                      </div>
                    )}
                    {/* Reprocess button for flagged rows without gap notes */}
                    {req.status === 'flagged' && !req.gap_notes && (
                      <button
                        onClick={() => handleReprocess(req)}
                        disabled={reprocessingId === req.id}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                          reprocessingId === req.id
                            ? 'bg-violet-500/20 text-violet-300 border-violet-500/30 cursor-wait'
                            : 'bg-violet-500/10 text-violet-400 border-violet-500/20 hover:bg-violet-500/20'
                        }`}
                      >
                        <RefreshCw size={12} className={reprocessingId === req.id ? 'animate-spin' : ''} />
                        {reprocessingId === req.id ? 'Reprocessing...' : 'Reprocess AI response'}
                      </button>
                    )}

                    {/* Brain response */}
                    {req.brain_response && (
                      <div className="rounded-lg border border-pink-500/20 bg-pink-500/5 p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] font-semibold text-pink-400 uppercase tracking-wider flex items-center gap-1.5">
                            <BrainCircuit size={10} />
                            Frankie Brain Response
                          </span>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation()
                              await rfpApi.updateRequirement(req.id, { ai_response: req.brain_response, source: 'cassidy', status: 'ai_draft' } as any)
                              showToast('Brain response applied as AI draft', 'success')
                              onUpdate()
                            }}
                            className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-pink-500/10 text-pink-400 border border-pink-500/20 hover:bg-pink-500/20 transition-colors"
                          >
                            <Check size={10} />
                            Use this response
                          </button>
                        </div>
                        <p className="text-sm text-[var(--text-secondary)] leading-relaxed whitespace-pre-wrap">{req.brain_response}</p>
                      </div>
                    )}

                    {/* Brain Review response — verdict markers are not response text */}
                    {req.brain_review_response && req.brain_review_response !== 'APPROVED' && req.brain_review_response !== 'FLAGGED' && (
                      <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] font-semibold text-emerald-400 uppercase tracking-wider flex items-center gap-1.5">
                            <Eye size={10} />
                            Brain Review (Corrected)
                          </span>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation()
                              await rfpApi.updateRequirement(req.id, { ai_response: req.brain_review_response, source: 'cassidy', status: 'ai_draft' } as any)
                              showToast('Brain review response applied as AI draft', 'success')
                              onUpdate()
                            }}
                            className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 transition-colors"
                          >
                            <Check size={10} />
                            Use this response
                          </button>
                        </div>
                        <p className="text-sm text-[var(--text-secondary)] leading-relaxed whitespace-pre-wrap">{req.brain_review_response}</p>
                      </div>
                    )}
                    {req.brain_review_response === 'APPROVED' && (
                      <div className="flex items-center gap-1.5 text-emerald-400 text-[10px] font-medium">
                        <Check size={10} />
                        Brain Review: Approved (factually accurate)
                      </div>
                    )}
                    {req.brain_review_response === 'FLAGGED' && (
                      <div className="flex items-center gap-1.5 text-amber-400 text-[10px] font-medium">
                        <Eye size={10} />
                        Brain Review: Flagged (needs human review — see gap notes)
                      </div>
                    )}

                    {/* Original response if present */}
                    {req.original_response && (
                      <div>
                        <span className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider">
                          Original Response (from Excel)
                        </span>
                        <p className="text-xs text-[var(--text-muted)] mt-1 whitespace-pre-wrap">{req.original_response}</p>
                      </div>
                    )}

                    {/* Fit + Assignment controls */}
                    <div className="flex items-center gap-4 pt-2 border-t border-[var(--border-color)]">
                      <div>
                        <label className="text-[10px] text-[var(--text-muted)] block mb-1">Fit Assessment</label>
                        <div className="flex gap-1">
                          {(['strong', 'weak', 'neutral', 'no_fit'] as const).map(f => {
                            const cfg = FIT_CONFIG[f]
                            return (
                              <button
                                key={f}
                                onClick={() => handleFitChange(req.id, f)}
                                className={`px-2 py-1 text-[10px] rounded-md border transition-colors ${
                                  req.fit === f
                                    ? `${cfg.bg} ${cfg.color} border-current`
                                    : 'border-[var(--border-color)] text-[var(--text-muted)] hover:bg-[var(--bg-tertiary)]'
                                }`}
                              >
                                {cfg.label}
                              </button>
                            )
                          })}
                        </div>
                      </div>
                      {selfEvalOptions && selfEvalOptions.length > 0 && (
                        <div>
                          <label className="text-[10px] text-[var(--text-muted)] block mb-1">Self-Evaluation</label>
                          <div className="flex gap-1">
                            {selfEvalOptions.map(opt => (
                              <button
                                key={opt}
                                onClick={async () => {
                                  await rfpApi.updateRequirement(req.id, { self_eval: req.self_eval === opt ? null : opt } as any)
                                  onUpdate()
                                }}
                                className={`px-2 py-1 text-[10px] rounded-md border transition-colors ${
                                  req.self_eval === opt
                                    ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/40'
                                    : 'border-[var(--border-color)] text-[var(--text-muted)] hover:bg-[var(--bg-tertiary)]'
                                }`}
                              >
                                {opt}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      <div>
                        <label className="text-[10px] text-[var(--text-muted)] block mb-1">Assign To</label>
                        <select
                          value={req.assigned_to || ''}
                          onChange={e => {
                            const member = teamMembers.find(m => m.email === e.target.value) || null
                            handleAssign(req.id, member)
                          }}
                          className="rounded-md border border-[var(--border-color)] bg-[var(--bg-secondary)] px-2 py-1 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-color)]"
                        >
                          <option value="">Unassigned</option>
                          {teamMembers.map(m => (
                            <option key={m.email} value={m.email}>
                              {m.name} ({m.expertise_areas.join(', ')})
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-8">
          <p className="text-sm text-[var(--text-muted)]">No requirements match your filters</p>
        </div>
      )}
    </div>
  )
}
